// @flow

import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';

import * as ABTest from 'app/entities/ABTest';
import MountUnmount from 'app/common/components/MountUnmount';

import type { AnyComponent, Dispatch } from 'app/common/types';

const Control = () => null;

/*
  Returns a wrapped component depending on which test is run.

  Usage for AbcTest (3 or More test cases):

  maybeRenderIfAbcTest will wrap the component(s) you provide for you after determining
  which test is running.

  You can optionally pass in a Fallback component by assigning a component
  as the 'control' case.

  const MaybeMyComponent = maybeRenderIfAbcTest('test-name', {
    'test-value-1': ComponentForTestValue1,
    'test-value-2': ComponentForTestValue2,
    'control': ComponentForControlCase
  });

  <MaybeMyComponent {...props}/>
*/
const ComponentWrapper = ({
  ActiveTestComponent,
  ...props
}: {
  ActiveTestComponent: AnyComponent,
}) => <ActiveTestComponent {...props} />;

type HocOptions = {|
  selectAdditionalBirthEventData?: (state: any) => {},
|};

export const maybeRenderIfAbcTest = (
  testName: string,
  testValueMap: { [string]: AnyComponent },
  options?: HocOptions
) => {
  const mapStateToProps = state => {
    const isEligible = ABTest.selectors.all.isUserEligibleForTest(
      testName,
      state
    );
    const hasControlComponent = !!testValueMap.control;
    // Edge case. If:
    // - the user is not eligible
    // - we passed in a `control` component in `testValueMap`
    // Then we want to always render that control component. Otherwise, we would:
    // - render `null` if the user is part of a non-control variant
    // - render `control` component if the user is part of the control variant
    // Which would be weird behavior!
    if (!isEligible && hasControlComponent) {
      return {
        shouldRender: true,
        ActiveTestComponent: testValueMap.control,
      };
    }

    const ActiveTestComponent = _.find(
      testValueMap,
      (component: AnyComponent, testValue: string) => {
        return ABTest.selectors.all.shouldRunVariantForTest(
          testValue,
          testName,
          state
        );
      }
    );

    const hasApplicableTest = !_.isUndefined(ActiveTestComponent);

    return {
      shouldRender: hasApplicableTest,
      ActiveTestComponent,
    };
  };

  const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
      onMount() {
        dispatch(
          ABTest.actions.trackBirthEventIfEligible({
            testName,
            selectAdditionalEventData:
              options && options.selectAdditionalBirthEventData,
          })
        );
      },
    };
  };

  return _.flowRight(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )
  )(props => {
    const Component = props.shouldRender ? ComponentWrapper : Control;
    return (
      <MountUnmount onMount={props.onMount}>
        <Component {..._.omit(props, 'onMount')} />
      </MountUnmount>
    );
  });
};

/*
   Returns HOC that renders component if there is an ABTest with that name that matches the test value

   Usage for AbTest (2 test cases):

     const MaybeMyComponent = maybeRenderIfAbTest('test-name', 'test-value')(MyComponent);

   You can pass a fallback component to render if the current Profile does NOT have an ab test
   with the given value:

  const MaybeMyComponent = maybeRenderIfAbTest(
    'test-name',
    'test-value'
  )(MyComponent, MyFallbackComponent);

  <MaybeMyComponent {...props} />
*/

const maybeRenderIfAbTest = (
  testName: string,
  testValue: string,
  options?: HocOptions
) => (Component: AnyComponent, FallbackComponent?: AnyComponent) => {
  return maybeRenderIfAbcTest(
    testName,
    {
      [testValue]: Component,
      ...(FallbackComponent ? { control: FallbackComponent } : {}),
    },
    options
  );
};

/**
 * Short-hand to render first given component if the user gets better errors, or the second (or none) otherwise.
 *
 * We'll be touching a lot of code, so this keeps it tidy and clear.
 */
export const maybeBetterErrors = (improvement: string) =>
  maybeRenderIfAbTest('better-errors', 'treatment', {
    selectAdditionalBirthEventData: () => ({
      improvement,
    }),
  });

export default maybeRenderIfAbTest;
